import React, { useState, useEffect, useMemo } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {
  getStockByBranch,
  deleteStock,
} from "../../services/OpeningStockApiServices";
import { useSelector } from "react-redux";
import OpeningStockModal from "../../components/transactions/modal/OpeningStockModal";
import { OpeningStockMaster } from "../../core/json/OpeningStockMaster";
import LoadingUtils from "../../core/utils/LoadingUtils";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, money, num } from "../../components/report/reportFormat";

const OpeningStock = () => {
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setloading] = useState(false);
  const [stockList, setStockList] = useState([]);

  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);

  const [permit, setpermit] = useState();
  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Opening Stock")
    );
  }, []);

  useEffect(() => {
    handlestockData();
  }, [branch]);

  const handlestockData = async () => {
    setloading(true);
    try {
      const response = await getStockByBranch(branch);
      const stockData = Array.isArray(response) ? response : [];
      setStockList(stockData);
      setloading(false);
    } catch (error) {
      setloading(false);
      console.error(
        "Failed to fetch Stock adjustments",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  const MySwal = withReactContent(Swal);

  // Handle the click event to delete a Stock adjustments
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const objStockTrn = new OpeningStockMaster();
          objStockTrn.id = obj?.id;
          objStockTrn.guid = obj?.guid;
          objStockTrn.branch = branch;
          objStockTrn.isDeleted = true;
          objStockTrn.updatedUser = `${userId?.id}`;
          const response = await deleteStock(objStockTrn);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The opening stock has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlestockData();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error(
            "Error deleting reason:",
            error?.response || "something went wrong"
          );
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.title || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditClick = async (guid) => {
    try {
      const response = getStockTrnByGuid(guid);

      setUpdateData(response);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching Stock adjustment by GUID",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  function getStockTrnByGuid(guid) {
    const filteredStock = stockList?.filter(
      (stockTrn) => stockTrn.guid === guid
    );

    return filteredStock[0];
  }

  /* ---------------- list ---------------- */
  // one flat record per opening stock entry; the keys match the columns
  const rows = useMemo(
    () =>
      stockList.map((entry) => {
        const lines = entry?.openingStockDetail || [];
        return {
          guid: entry.guid,
          raw: entry,
          date: dateOf(entry?.date),
          billNo: entry?.entryNo || "",
          refNo: entry?.referenceNo || "",
          remarks: entry?.remarks || "",
          items: lines.length,
          qty: lines.reduce((a, l) => a + num(l?.quantity), 0),
          lines,
        };
      }),
    [stockList]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry No", type: "doc" },
      { k: "remarks", t: "Remarks" },
      { k: "refNo", t: "Reference No", off: true },
      { k: "items", t: "Items", type: "int", off: true },
      { k: "qty", t: "Quantity", type: "int", off: true },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Opening Stock" }], []);
  const filters = useMemo(() => [], []);

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.remarks) ||
      hit(r.refNo) ||
      hit(r.date) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code) || hit(l?.batch))
    );
  };

  const drawerContent = (r) => ({
    title: r.billNo ? `Opening stock ${r.billNo}` : "Opening stock",
    subtitle: [r.billNo, dmy(r.date), r.refNo].filter(Boolean).join("  ·  "),
    avatarKey: r.billNo || "OS",
    meta: [
      { k: "Date", v: dmy(r.date) },
      { k: "Reference No", v: r.refNo },
      { k: "Remarks", v: r.remarks },
      { k: "Items", v: intg(r.items) },
    ],
    lines: {
      title: "Stock lines",
      columns: [
        { t: "Item" },
        { t: "Batch" },
        { t: "Qty", align: "right" },
        { t: "Price", align: "right" },
      ],
      rows: r.lines.map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        l?.batch || "—",
        intg(l?.quantity),
        money(l?.price),
      ]),
    },
    totals: [{ l: "Total quantity", v: intg(r.qty) }],
  });

  return (
    <TransactionScreen
      title="Opening Stock"
      subtitle="Manage Opening stock here"
      unit="entries"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, remarks, reference, item…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "billNo:-1", t: "Entry no (high–low)" },
        { v: "billNo:1", t: "Entry no (low–high)" },
      ]}
      exportName="Opening stock"
      onRefresh={handlestockData}
      canAdd={permit?.add}
      newLabel="New opening stock"
      onNew={() => {
        setModalMode("add");
        setModalOpen(true);
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit opening stock"
      canDelete={permit?.deletion}
      // the old list passed a reshaped row without `id`; kept as it was
      onDelete={(r) => handleDeleteClick({ guid: r.guid })}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      <LoadingUtils loading={loading} />
      {modalOpen && (
        <OpeningStockModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlestockData();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default OpeningStock;
